import { useEffect, useMemo, useState } from 'react';
import { ChatDock } from './ChatDock';
import { updatePrefs } from '../../lib/chat';
import { setCorner } from '../../lib/corner';
import { sound } from '../../lib/sound';
import { loadProgress } from '../../progress';
import { useAccount } from '../../state/useAccount';
import { useStrings } from '../../state/useLocale';
import { Toast, useToast } from '../../ui/toast';

/**
 * The chat bubble, before there is a chat behind it.
 *
 * The pages outside the app — the landing page, a public profile, a
 * certificate, the Blobor showcase — are the ones strangers arrive on, and the
 * reason they never mounted the dock still holds: the dock opens a socket the
 * moment it mounts and joins a room with people in it (state/useChat.ts), so a
 * dock on those pages would put every passer-by into the room's occupancy and
 * spend a connection on pages that exist to be cheap.
 *
 * But a corner with no bubble in it is a corner that says there is nobody to
 * talk to, which is the opposite of what the bubble is for (styles/chat.css,
 * "the one interface element allowed to be loud"). So these pages draw the
 * bubble and nothing else: same class, same glyph, same place. Pressing it —
 * or Ctrl+/, which its title promises — is the ask for a person, and only
 * then does the real dock mount, already open, and take the bubble's place.
 *
 * Open is written to the dock's own preferences rather than passed as a prop,
 * because that is where the dock reads it from on mount (lib/chat.ts), and it
 * is the truth anyway: this browser has just opened the chat.
 */
export function ChatDoor({ onOpen }: { onOpen(): void }) {
  const s = useStrings().chat.dock;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // The dock's own shortcut, on the dock's own terms (ChatDock.tsx).
      if (e.key !== '/' || !(e.metaKey || e.ctrlKey) || e.altKey || e.defaultPrevented) return;
      e.preventDefault();
      enter();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  function enter() {
    updatePrefs({ open: true });
    // Ctrl+/ works with the corner folded, and a panel whose bubble is folded
    // away is a door with no handle — the dock's own `show` makes the same
    // move for the same reason.
    setCorner('shown');
    onOpen();
  }

  return (
    <button
      type="button"
      className="chat-toggle"
      aria-expanded={false}
      aria-label={s.toggle(false, 0, 0)}
      title={s.toggleWhy(false, 0, 0)}
      onClick={() => {
        sound.click();
        enter();
      }}
    >
      <svg viewBox="0 0 16 16" aria-hidden="true" width="20" height="20">
        <path
          d="M3 3.5h10a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H7.5L4.5 14v-2.5H3a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.35"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

/**
 * The door and what is behind it, for a page that has neither an account nor a
 * progress record of its own to lend the dock.
 *
 * Everything the dock needs is fetched only once the door is pressed: the
 * account (one request to the Worker), this browser's record (a synchronous
 * read of local storage), and a toast for the account to report through —
 * these pages have none, and a sign-in that silently failed would be worse
 * than no sign-in button.
 */
export function PageChat() {
  const [live, setLive] = useState(false);
  return live ? <LiveDock /> : <ChatDoor onOpen={() => setLive(true)} />;
}

function LiveDock() {
  const { toast, notify, clear } = useToast();
  const account = useAccount(notify);
  const progress = useMemo(() => loadProgress(), []);
  return (
    <>
      <ChatDock progress={progress} mission={null} notify={notify} account={account} />
      <Toast toast={toast} onDone={clear} />
    </>
  );
}
